import React, { useState, useRef, useEffect } from 'react';
// ScrollText aliased as Receipt: lucide's Receipt icon contains a "$" glyph — app must stay currency-neutral
import { Send, Bot, User, Loader, AlertCircle, TrendingUp, CreditCard, Clock, Coins, AlertTriangle, Building2, MessageSquare, Plus, ChevronLeft, ChevronRight, X, ScrollText as Receipt, Zap } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { config } from '../utils/config';
import * as chatApi from '../api/chat';
import { ExpenseDraft } from '../api/expenses';
import { useSubscription } from '../hooks/useSubscription';
import ExpenseDraftCard from './ai/ExpenseDraftCard';
import ChatMarkdown from './ai/ChatMarkdown';
import { useToast } from './ui/Toast';
import { errorMessage } from '../utils/errors';
import type { AIInvoiceContext } from '../types';

interface Message {
  role: 'assistant' | 'user';
  content: string;
  error?: boolean;
  timestamp?: Date;
  suggestions?: string[];
  // Present when the assistant parsed an expense out of this turn. Lives only
  // in memory: the session transcript stores `content` alone, so a reloaded
  // conversation shows the recap text without a re-postable card.
  expenseDraft?: ExpenseDraft;
}

interface SuggestedQuestion {
  icon: React.ReactNode;
  text: string;
  query: string;
}

/**
 * Build an optimized context payload that stays under the character limit.
 * Reduces entities to essential fields and truncates large text.
 */
const buildSmartContext = (contextData: any, source?: string): string | undefined => {
  if (!contextData) return undefined;

  // An expense is a flat record, not a document + entities payload. The
  // document-shaped reduction below rebuilds `optimized` from documentId /
  // filename / entities, so every expense field except `status` would be
  // dropped before the request left the browser.
  if (source === 'expenses') {
    return JSON.stringify({ kind: 'expense', ...contextData });
  }

  // A created invoice has no document or entities either; the reduction below
  // turned it into "{}". The backend loads the full record from its id.
  if (contextData.documentId === undefined && contextData.invoiceNumber) {
    return JSON.stringify({
      kind: 'created_invoice',
      invoiceNumber: contextData.invoiceNumber,
      amount: contextData.amount,
      currency: contextData.currency,
    });
  }

  const MAX_CONTEXT_LENGTH = 3500; // Leave buffer for JSON overhead

  try {
    // Extract only essential fields based on the source/action type
    const optimized: any = {
      documentId: contextData.documentId,
      filename: contextData.filename,
    };

    // Add status if available
    if (contextData.status) {
      optimized.status = contextData.status;
    }

    // Add confidence if available
    if (contextData.confidence !== undefined) {
      optimized.confidence = contextData.confidence;
    }

    // Process entities - only include essential info
    if (contextData.entities && Array.isArray(contextData.entities)) {
      optimized.entities = contextData.entities.slice(0, 15).map((entity: any) => ({
        type: entity.type || entity.entity_type,
        text: typeof entity.text === 'string' ? entity.text.substring(0, 100) : entity.text,
        confidence: entity.confidence,
        // Truncate context field
        context: entity.context ? entity.context.substring(0, 50) + '...' : undefined
      }));

      // Add count if we truncated
      if (contextData.entities.length > 15) {
        optimized.totalEntities = contextData.entities.length;
        optimized.entitiesShown = 15;
      }
    }

    // For specific actions, add relevant context
    if (source === 'dashboard_missing_fields') {
      // Find which required fields are missing
      const requiredFields = ['INVOICE_NUMBER', 'INVOICE_DATE', 'TOTAL_AMOUNT', 'SELLER_NAME', 'BUYER_NAME'];
      const foundTypes = new Set(
        (contextData.entities || []).map((e: any) => e.type || e.entity_type)
      );
      optimized.missingFields = requiredFields.filter(f => !foundTypes.has(f));
    }

    if (source === 'dashboard_validation') {
      // Include low confidence entities for review
      optimized.lowConfidenceFields = (contextData.entities || [])
        .filter((e: any) => e.confidence && e.confidence < 0.8)
        .slice(0, 5)
        .map((e: any) => ({
          type: e.type || e.entity_type,
          text: typeof e.text === 'string' ? e.text.substring(0, 50) : e.text,
          confidence: e.confidence
        }));
    }

    const result = JSON.stringify(optimized);

    // If still too large, further reduce
    if (result.length > MAX_CONTEXT_LENGTH) {
      // Remove entities and just keep summary
      const minimal = {
        documentId: optimized.documentId,
        filename: optimized.filename,
        status: optimized.status,
        entityCount: contextData.entities?.length || 0,
        missingFields: optimized.missingFields,
        message: 'Context truncated due to size. Please analyze document ' + optimized.documentId
      };
      return JSON.stringify(minimal);
    }

    return result;
  } catch (e) {
    console.error('Error building smart context:', e);
    // Fallback to minimal context
    return JSON.stringify({
      documentId: contextData.documentId,
      filename: contextData.filename
    });
  }
};

const AIPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm ViewPoint, your intelligent invoice assistant. I can help you find overdue invoices, spot missing information, analyze vendor spending, and answer questions about your documents. What would you like to know?",
      timestamp: new Date()
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Session management state
  const [sessions, setSessions] = useState<chatApi.ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showSessionList, setShowSessionList] = useState(false);

  // Quick actions popover
  const [showQuickActions, setShowQuickActions] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement>(null);

  // Expenses is a Trial/Advanced feature; hide its prompts from plans that
  // would only get an upgrade message back.
  const { isExpensesEligible } = useSubscription();

  const expenseQuestions: SuggestedQuestion[] = isExpensesEligible ? [
    {
      icon: <Plus className="w-4 h-4" />,
      text: "Record an expense",
      query: "Record an expense of 120 EUR for software"
    },
    {
      icon: <CreditCard className="w-4 h-4" />,
      text: "Expenses this month",
      query: "How much have I spent this month, broken down by category?"
    }
  ] : [];

  const suggestedQuestions: SuggestedQuestion[] = [
    ...expenseQuestions,
    {
      icon: <TrendingUp className="w-4 h-4" />,
      text: "This month's summary",
      query: "Summarise my invoicing this month: total billed and received, paid vs unpaid, overdue, and anything that needs my attention."
    },
    {
      icon: <Clock className="w-4 h-4" />,
      text: "Overdue invoices",
      query: "Show me all overdue invoices that need payment"
    },
    {
      icon: <AlertTriangle className="w-4 h-4" />,
      text: "Missing information",
      query: "Find invoices with missing or incomplete information"
    },
    {
      icon: <Building2 className="w-4 h-4" />,
      text: "Vendor breakdown",
      query: "Show me total amounts by vendor this month"
    },
    {
      icon: <Coins className="w-4 h-4" />,
      text: "High-value invoices",
      query: "Show me invoices over 5,000 that need review"
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Also on `loading`: the reply lands while "Thinking…" is still shown, and
  // removing that row a moment later cut the smooth scroll short, leaving
  // long answers (tables) half off-screen.
  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // State for context data from ViewPoint chips
  const [contextData, setContextData] = useState<any>(null);
  const [contextSource, setContextSource] = useState<string | undefined>(undefined);

  // State for invoice context badge (persists throughout conversation)
  const [invoiceContext, setInvoiceContext] = useState<AIInvoiceContext | null>(null);

  // Handle pre-filled messages from navigation (e.g., from ViewPoint chips)
  useEffect(() => {
    const state = location.state as {
      prefillMessage?: string;
      contextData?: any;
      source?: string;
      invoiceContext?: AIInvoiceContext;
    } | null;

    if (state?.prefillMessage) {
      setInput(state.prefillMessage);

      // Store context data and source if provided
      if (state?.contextData) {
        setContextData(state.contextData);
      }
      if (state?.source) {
        setContextSource(state.source);
      }

      // Set invoice context badge if provided (persists throughout conversation)
      if (state?.invoiceContext) {
        setInvoiceContext(state.invoiceContext);
      }

      // Auto-focus the input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      // Clear the navigation state to prevent re-filling on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Load chat sessions on mount
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const userSessions = await chatApi.getChatSessions();
        setSessions(userSessions);

        // If there's a most recent session, load it
        if (userSessions.length > 0 && !currentSessionId) {
          const mostRecent = userSessions[0];
          setCurrentSessionId(mostRecent.session_id);
          await loadSessionMessages(mostRecent.session_id);
        }
      } catch (error) {
        toast.error(errorMessage(error, 'Couldn’t load your previous chats.'));
      }
    };

    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages for a specific session
  const loadSessionMessages = async (sessionId: string) => {
    try {
      const sessionMessages = await chatApi.getSessionMessages(sessionId);

      // Convert to local Message format
      const loadedMessages: Message[] = sessionMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
      }));

      setMessages(loadedMessages);
    } catch (error) {
      toast.error(errorMessage(error, 'Couldn’t load this conversation.'));
    }
  };

  // Close quick actions popover on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target as Node)) {
        setShowQuickActions(false);
      }
    };
    if (showQuickActions) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showQuickActions]);

  const handleSuggestedQuestion = (query: string) => {
    setInput(query);
    setShowQuickActions(false);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    // Store context for this request, then clear it
    const requestContext = contextData;
    const requestSource = contextSource;
    setContextData(null); // Clear context after using it once
    setContextSource(undefined);

    // The invoice this message is about: the pinned one, else the document a
    // page handed over with the prefill (that context is sent once only).
    const documentId = invoiceContext?.documentId
      ?? (invoiceContext ? undefined : requestContext?.documentId);
    const createdInvoiceId = invoiceContext?.createdInvoiceId;

    // Ensure we have a session
    let sessionId = currentSessionId;
    if (!sessionId) {
      try {
        const newSession = await chatApi.createChatSession({ title: 'New Chat' });
        sessionId = newSession.session_id;
        setCurrentSessionId(sessionId);
        setSessions(prev => [newSession, ...prev]);
      } catch (error) {
        console.error('Failed to create session:', error);
      }
    }

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${config.API_URL}${config.endpoints.ai}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage.content,
          context: buildSmartContext(requestContext, requestSource),
          ...(typeof documentId === 'number' && documentId > 0 ? { document_id: documentId } : {}),
          ...(typeof createdInvoiceId === 'number' && createdInvoiceId > 0
            ? { created_invoice_id: createdInvoiceId }
            : {}),
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        const errorText = await response.text();
        let errorMessage = 'Server error';

        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            // Handle FastAPI validation errors (422) which return array of details
            if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) =>
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (typeof errorData.detail === 'object') {
              errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const responseText = await response.text();

      if (responseText && responseText.trim()) {
        try {
          const data = JSON.parse(responseText);

          // Check if response contains an error (e.g., query limit reached)
          if (data.error) {
            // Log detailed error info for debugging
            console.error('AI Error:', data.error, data.metadata);
            setError(data.error);
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.response,
              suggestions: data.suggestions,
              error: true,
              timestamp: new Date()
            }]);
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.response,
              suggestions: data.suggestions,
              expenseDraft: data.metadata?.expense_draft,
              timestamp: new Date()
            }]);
          }

          // Update dynamic suggestions if provided
          if (data.suggestions && data.suggestions.length > 0) {
            setDynamicSuggestions(data.suggestions);
          }

          // Save messages to session
          if (sessionId) {
            try {
              // Save user message
              await chatApi.addMessage(sessionId, {
                role: 'user',
                content: userMessage.content,
              });

              // Save assistant response
              await chatApi.addMessage(sessionId, {
                role: 'assistant',
                content: data.response,
              });

              // Refresh sessions list to update timestamps/counts
              const updatedSessions = await chatApi.getChatSessions();
              setSessions(updatedSessions);
            } catch (saveError) {
              console.error('Failed to save messages:', saveError);
            }
          }
        } catch (parseError) {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Empty response from AI service');
      }

    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or contact support if the issue persists.',
        error: true,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // Session management functions
  const handleNewChat = async () => {
    try {
      const newSession = await chatApi.createChatSession({ title: 'New Chat' });
      setCurrentSessionId(newSession.session_id);
      setSessions(prev => [newSession, ...prev]);
      setMessages([
        {
          role: 'assistant',
          content: "Hi! I'm ViewPoint, your intelligent invoice assistant. I can help you find overdue invoices, spot missing information, analyze vendor spending, and answer questions about your documents. What would you like to know?",
          timestamp: new Date()
        }
      ]);
      setDynamicSuggestions([]);
    } catch (error) {
      // "New Chat" looked completely inert when this failed.
      toast.error(errorMessage(error, 'Couldn’t start a new chat. Try again.'));
    }
  };

  const handleSwitchSession = async (sessionId: string) => {
    setCurrentSessionId(sessionId);
    await loadSessionMessages(sessionId);
    setShowSessionList(false);
  };

  return (
    <div className="flex-1 flex h-[calc(100vh-8rem)]">
      {/* Session Sidebar */}
      {showSessionList && (
        <div className="w-64 border-r border-[var(--color-border)] dark:border-coral-500/20 bg-white dark:bg-black/20 overflow-y-auto flex flex-col shadow-md dark:shadow-none">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-[var(--color-border)] dark:border-coral-500/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)] dark:text-foreground-secondary flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-coral-600 dark:text-coral-400" />
                Chat History
              </h3>
              <button
                onClick={() => setShowSessionList(false)}
                className="text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)] dark:text-coral-400 dark:hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleNewChat}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-coral-600 hover:bg-coral-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>

          {/* Session List */}
          <div className="flex-1 overflow-y-auto p-2">
            {sessions.length === 0 ? (
              <div className="text-[var(--color-foreground-subtle)] dark:text-foreground-muted text-xs text-center py-8">
                No chat history yet
              </div>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => handleSwitchSession(session.session_id)}
                  className={`w-full text-left p-3 rounded-xl mb-1.5 transition-all ${
                    session.session_id === currentSessionId
                      ? 'bg-coral-50 dark:bg-coral-500/20 border border-coral-200 dark:border-coral-500/30'
                      : 'hover:bg-background-secondary dark:hover:bg-coral-500/10 border border-transparent'
                  }`}
                >
                  <div className="text-sm font-medium text-[var(--color-foreground)] dark:text-foreground-secondary truncate mb-0.5">
                    {session.title || 'New Chat'}
                  </div>
                  <div className="text-xs text-[var(--color-foreground-muted)] dark:text-foreground-muted">
                    {new Date(session.updated_at).toLocaleDateString()} · {session.message_count || 0} messages
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)] dark:border-coral-500/20 bg-white dark:bg-black/10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSessionList(!showSessionList)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)] hover:bg-background-secondary dark:text-foreground-muted dark:hover:text-foreground-secondary dark:hover:bg-coral-500/10 transition-all text-sm font-medium"
            >
              {showSessionList ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <MessageSquare className="w-4 h-4" />
              {sessions.length > 0 && <span className="text-xs opacity-70">({sessions.length})</span>}
            </button>

            {/* Invoice Context Badge */}
            {invoiceContext && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-coral-50 dark:bg-coral-500/20 border border-coral-200 dark:border-coral-500/30 rounded-full">
                <Receipt className="w-3.5 h-3.5 text-coral-600 dark:text-coral-400" />
                <span className="text-xs font-medium text-coral-700 dark:text-foreground-secondary">
                  Invoice {invoiceContext.invoiceNumber || `#${invoiceContext.documentId ?? invoiceContext.createdInvoiceId}`}
                  {invoiceContext.clientName && (
                    <span className="opacity-60"> · {invoiceContext.clientName}</span>
                  )}
                </span>
                <button
                  onClick={() => setInvoiceContext(null)}
                  className="ml-0.5 p-0.5 hover:bg-coral-200 dark:hover:bg-coral-500/30 rounded-full transition-colors"
                  title="Clear invoice context"
                >
                  <X className="w-3 h-3 text-coral-500 dark:text-coral-400" />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={handleNewChat}
            className="flex items-center gap-2 px-4 py-1.5 bg-coral-600 hover:bg-coral-700 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Error Banner */}
      {error && (
        <div className="m-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-200 text-sm mb-2">{error}</p>
              {(error.toLowerCase().includes('trial') ||
                error.toLowerCase().includes('subscription') ||
                error.toLowerCase().includes('upgrade') ||
                error.toLowerCase().includes('limit reached') ||
                error.toLowerCase().includes('ai features')) && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="flex items-center gap-2 px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <CreditCard className="w-4 h-4" />
                  View Pricing Plans
                </button>
              )}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 shrink-0"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Chat Container */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-white dark:bg-transparent">

        {/* Messages */}
        {messages.map((message, index) => {
          const isLastAssistantMessage =
            message.role === 'assistant' &&
            index === messages.length - 1 &&
            !loading;

          return (
            <div key={index}>
              <div
                className={`flex items-start gap-3 max-w-3xl ${
                  message.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'
                }`}
              >
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  message.role === 'user'
                    ? 'bg-coral-600'
                    : message.error
                      ? 'bg-red-100 dark:bg-red-500/20'
                      : 'bg-purple-100 dark:bg-purple-500/20'
                }`}>
                  {message.role === 'assistant' ? (
                    <Bot className={`w-4 h-4 ${message.error ? 'text-red-500' : 'text-purple-600 dark:text-purple-400'}`} />
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>

                {/* Bubble */}
                <div className={`flex-1 min-w-0 ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-full text-left px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-coral-600 text-white rounded-tr-sm'
                      : message.error
                        ? 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300 rounded-tl-sm'
                        : 'bg-background-secondary dark:bg-coral-500/10 border border-[var(--color-border)] dark:border-coral-500/20 text-[var(--color-foreground)] dark:text-foreground rounded-tl-sm'
                  }`}>
                    {message.role === 'assistant' ? <ChatMarkdown content={message.content} /> : message.content}
                  </div>
                  {message.timestamp && (
                    <div className="text-xs text-[var(--color-foreground-subtle)] dark:text-foreground-muted mt-1 px-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>

              {/* Editable draft for an expense parsed out of this turn */}
              {message.expenseDraft && <ExpenseDraftCard parsed={message.expenseDraft} />}

              {/* Inline follow-up suggestions under the last assistant message */}
              {isLastAssistantMessage && dynamicSuggestions.length > 0 && (
                <div className="ml-11 mt-2 flex flex-wrap gap-2">
                  {dynamicSuggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestedQuestion(suggestion)}
                      className="px-3 py-1.5 bg-white dark:bg-coral-600/20 hover:bg-coral-50 dark:hover:bg-coral-600/30 border border-[var(--color-border)] dark:border-coral-400/30 rounded-full text-xs font-medium text-[var(--color-foreground-secondary)] dark:text-foreground-secondary transition-all hover:border-coral-400"
                      disabled={loading}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-start gap-3 max-w-3xl mr-auto">
            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
              <Bot className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="px-4 py-3 bg-background-secondary dark:bg-coral-500/10 border border-[var(--color-border)] dark:border-coral-500/20 rounded-2xl rounded-tl-sm flex items-center gap-2 text-[var(--color-foreground-muted)] dark:text-foreground-muted">
              <Loader className="w-3.5 h-3.5 animate-spin text-purple-600 dark:text-purple-400" />
              <span className="text-sm">Thinking…</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div className="px-5 py-4 border-t border-[var(--color-border)] dark:border-coral-500/20 bg-white dark:bg-black/20">
        <div className="max-w-4xl mx-auto relative" ref={quickActionsRef}>

          {/* Quick Actions Popover */}
          {showQuickActions && (
            <div className="absolute bottom-full mb-3 left-0 right-0 bg-white dark:bg-background-secondary border border-[var(--color-border)] dark:border-coral-500/30 rounded-2xl shadow-xl dark:shadow-coral-900/20 p-4 z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)] dark:text-foreground-muted">Quick Actions</span>
                <button
                  onClick={() => setShowQuickActions(false)}
                  className="p-1 rounded-lg hover:bg-background-secondary dark:hover:bg-coral-500/10 text-[var(--color-foreground-muted)] dark:text-coral-400 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestion(question.query)}
                    className="flex items-center gap-3 p-3 bg-background-secondary dark:bg-coral-500/10 hover:bg-coral-50 dark:hover:bg-coral-500/20 border border-[var(--color-border)] dark:border-coral-500/20 rounded-xl transition-all text-left"
                    disabled={loading}
                  >
                    <div className="w-7 h-7 rounded-lg bg-coral-100 dark:bg-coral-500/20 flex items-center justify-center flex-shrink-0">
                      <div className="text-coral-600 dark:text-coral-400">{question.icon}</div>
                    </div>
                    <span className="text-sm font-medium text-[var(--color-foreground)] dark:text-foreground-secondary">{question.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="flex gap-3 items-center bg-background-secondary dark:bg-coral-500/10 border border-[var(--color-border)] dark:border-coral-500/20 rounded-2xl px-3 py-2 focus-within:border-coral-500 focus-within:ring-2 focus-within:ring-coral-500/10 transition-all">
              {/* Quick Actions toggle */}
              <button
                type="button"
                onClick={() => setShowQuickActions(v => !v)}
                title="Quick Actions"
                className={`flex items-center justify-center w-8 h-8 rounded-xl transition-all flex-shrink-0 ${
                  showQuickActions
                    ? 'bg-coral-600 text-white'
                    : 'bg-coral-100 dark:bg-coral-500/20 text-coral-600 dark:text-coral-400 hover:bg-coral-200 dark:hover:bg-coral-500/30'
                }`}
              >
                <Zap className="w-4 h-4" />
              </button>

              <input
                ref={inputRef}
                data-ai-input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your documents…"
                className="flex-1 bg-transparent border-none outline-none text-sm text-[var(--color-foreground)] dark:text-foreground placeholder-[var(--color-foreground-subtle)] dark:placeholder-coral-400/60"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-coral-600 hover:bg-coral-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-all shadow-sm hover:shadow-md"
              >
                {loading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {loading ? 'Sending' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AIPage;